import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useAuth } from './AuthContext';
import axios from 'axios';
import toast from 'react-hot-toast';

const AppDataContext = createContext();

export const useAppData = () => {
  const context = useContext(AppDataContext);
  if (!context) {
    throw new Error('useAppData must be used within an AppDataProvider');
  }
  return context;
};

export const AppDataProvider = ({ children }) => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  // États globaux
  const [selectedMember, setSelectedMember] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [globalStats, setGlobalStats] = useState({
    totalMembers: 384,
    activeMembers: 312,
    totalRevenue: 875000,
    equipmentStatus: "43/48",
    todayAttendance: 45,
    lowStockItems: 3
  });

  // Mock data pour les abonnements
  const mockSubscriptionPlans = [
    {
      id: 1,
      name: 'Pass Journalier',
      type: 'daily',
      duration: '1 jour',
      price: 500,
      description: 'Accès complet pour une journée',
      features: ['Accès à tous les équipements', 'Vestiaires', 'Douches'],
      isPopular: false,
      subscriberCount: 8
    },
    {
      id: 2,
      name: 'Abonnement Mensuel',
      type: 'monthly',
      duration: '1 mois',
      price: 8500,
      description: 'Le plan le plus populaire',
      features: ['Accès illimité', 'Tous équipements', 'Vestiaires & Douches', 'Coaching gratuit'],
      isPopular: true,
      subscriberCount: 230
    },
    {
      id: 3,
      name: 'Abonnement Trimestriel',
      type: 'quarterly',
      duration: '3 mois',
      price: 24000,
      description: 'Économisez 6% sur l\'abonnement mensuel',
      features: ['Accès illimité', 'Tous équipements', 'Vestiaires & Douches', 'Coaching gratuit', '1 session personnalisée'],
      isPopular: false,
      subscriberCount: 45
    },
    {
      id: 4,
      name: 'Abonnement Annuel',
      type: 'annual',
      duration: '12 mois',
      price: 85000,
      description: 'Meilleure économie sur l\'année',
      features: ['Accès illimité', 'Tous équipements', 'Vestiaires & Douches', 'Coaching personnel', 'Plan nutritionnel', '2 mois gratuits'],
      isPopular: false,
      subscriberCount: 29
    }
  ];

  const mockSubscriptions = [
    {
      id: 1,
      memberId: 101,
      memberName: 'Sofiane Belhadj',
      planId: 2,
      planName: 'Abonnement Mensuel',
      type: 'monthly',
      startDate: '2024-07-01',
      endDate: '2024-08-01',
      price: 8500,
      paymentMethod: 'cash',
      status: 'active',
      notes: 'Première inscription'
    },
    {
      id: 2,
      memberId: 102,
      memberName: 'Samira Khelif',
      planId: 4,
      planName: 'Abonnement Annuel',
      type: 'annual',
      startDate: '2024-06-15',
      endDate: '2025-06-15',
      price: 85000,
      paymentMethod: 'bank',
      status: 'active',
      notes: 'Client fidèle'
    },
    {
      id: 3,
      memberId: 103,
      memberName: 'Mehdi Larbi',
      planId: 3,
      planName: 'Abonnement Trimestriel',
      type: 'quarterly',
      startDate: '2024-05-10',
      endDate: '2024-08-10',
      price: 24000,
      paymentMethod: 'cash',
      status: 'active',
      notes: 'Paie en espèces'
    },
    {
      id: 4,
      memberId: 104,
      memberName: 'Yasmine Benali',
      planId: 2,
      planName: 'Abonnement Mensuel',
      type: 'monthly',
      startDate: '2024-07-05',
      endDate: '2024-08-05',
      price: 8500,
      paymentMethod: 'card',
      status: 'active',
      notes: 'Préfère venir le matin'
    },
    {
      id: 5,
      memberId: 105,
      memberName: 'Omar Merzougui',
      planId: 2,
      planName: 'Abonnement Mensuel',
      type: 'monthly',
      startDate: '2024-07-03',
      endDate: '2024-07-25',
      price: 8500,
      paymentMethod: 'cash',
      status: 'suspended',
      notes: 'A demandé une suspension temporaire'
    },
    {
      id: 6,
      memberId: 106,
      memberName: 'Amel Hadj',
      planId: 1,
      planName: 'Pass Journalier',
      type: 'daily',
      startDate: '2024-07-19',
      endDate: '2024-07-20',
      price: 500,
      paymentMethod: 'cash',
      status: 'active',
      notes: 'Première visite, potentiellement intéressée par un abonnement mensuel'
    },
    {
      id: 7,
      memberId: 107,
      memberName: 'Karim Ziani',
      planId: 4,
      planName: 'Abonnement Annuel',
      type: 'annual',
      startDate: '2024-01-10',
      endDate: '2025-01-10',
      price: 85000,
      paymentMethod: 'bank',
      status: 'active',
      notes: 'Athlète semi-professionnel'
    },
    {
      id: 8,
      memberId: 108,
      memberName: 'Fatiha Mansouri',
      planId: 2,
      planName: 'Abonnement Mensuel',
      type: 'monthly',
      startDate: '2024-06-20',
      endDate: '2024-07-20',
      price: 8500,
      paymentMethod: 'cash',
      status: 'active',
      notes: 'Expire cette semaine'
    }
  ];

  // Mock data pour l'interconnexion
  const mockEmployees = [
    {
      id: 1,
      firstName: 'Karim',
      lastName: 'Benali',
      photo: null,
      department: 'management',
      position: 'Directeur',
      email: 'karim.benali@example.com',
      phone: '+213 555 123 456',
      address: '15 Rue des Frères Bouadou, Alger',
      joinDate: '2021-03-10',
      salary: 120000,
      status: 'active',
      schedule: 'Lun-Ven: 9h-17h',
      socialSecurity: 'SS12345678',
      emergencyContact: 'Samira Benali - +213 555 789 123',
      bankDetails: 'Banque ABC - 123456789',
      education: 'Master en gestion sportive',
      certifications: ['Gestion d\'entreprise', 'Management du sport']
    },
    {
      id: 2,
      firstName: 'Fatima',
      lastName: 'Zahra',
      photo: null,
      department: 'reception',
      position: 'Réceptionniste',
      email: 'fatima.z@example.com',
      phone: '+213 555 456 789',
      address: '27 Boulevard Mohamed V, Alger',
      joinDate: '2022-01-15',
      salary: 60000,
      status: 'active',
      schedule: 'Lun-Sam: 8h-14h / 14h-20h (alternance)',
      socialSecurity: 'SS23456789',
      emergencyContact: 'Ahmed Zahra - +213 555 111 222',
      bankDetails: 'Banque XYZ - 987654321',
      education: 'Baccalauréat + 2 en administration',
      certifications: ['Service client', 'Premiers secours']
    },
    {
      id: 3,
      firstName: 'Ahmed',
      lastName: 'Rahmani',
      photo: null,
      department: 'maintenance',
      position: 'Technicien de maintenance',
      email: 'ahmed.r@example.com',
      phone: '+213 555 789 456',
      address: '8 Rue Didouche Mourad, Alger',
      joinDate: '2022-05-20',
      salary: 70000,
      status: 'active',
      schedule: 'Lun-Ven: 7h-15h',
      socialSecurity: 'SS34567890',
      emergencyContact: 'Nadia Rahmani - +213 555 333 444',
      bankDetails: 'Banque DEF - 246813579',
      education: 'BTS Électromécanique',
      certifications: ['Maintenance équipements sportifs', 'Électricité bâtiment']
    },
    {
      id: 4,
      firstName: 'Amina',
      lastName: 'Belkacem',
      photo: null,
      department: 'finance',
      position: 'Comptable',
      email: 'amina.b@example.com',
      phone: '+213 555 987 654',
      address: '42 Avenue Ali Khodja, Alger',
      joinDate: '2022-02-01',
      salary: 85000,
      status: 'vacation',
      schedule: 'Lun-Jeu: 9h-17h',
      socialSecurity: 'SS45678901',
      emergencyContact: 'Youcef Belkacem - +213 555 555 666',
      bankDetails: 'Banque GHI - 135792468',
      education: 'Master en comptabilité',
      certifications: ['Comptabilité', 'Fiscalité algérienne']
    }
  ];

  const mockProducts = [
    {
      id: 1,
      name: 'Protéine Whey 1kg',
      image: null,
      category: 'supplements',
      price: 3500,
      stock: 25,
      lowStockThreshold: 5,
      sku: 'PROT-WHY-001',
      barcode: '123456789012',
      supplier: 'NutriSport',
      costPrice: 2500,
      description: 'Protéine de whey de haute qualité, 25g de protéine par portion',
      location: 'Étagère A1'
    },
    {
      id: 2,
      name: 'T-shirt Sport Homme M',
      image: null,
      category: 'clothing',
      price: 1200,
      stock: 8,
      lowStockThreshold: 5,
      sku: 'TSH-SPT-M',
      barcode: '123456789013',
      supplier: 'SportWear',
      costPrice: 800,
      description: 'T-shirt respirant pour homme taille M',
      location: 'Étagère B2'
    },
    {
      id: 3,
      name: 'Créatine Monohydrate 500g',
      image: null,
      category: 'supplements',
      price: 2200,
      stock: 3,
      lowStockThreshold: 5,
      sku: 'CRT-MNH-500',
      barcode: '123456789014',
      supplier: 'NutriSport',
      costPrice: 1500,
      description: 'Créatine monohydrate pure à 100%, 5g par portion',
      location: 'Étagère A2'
    },
    {
      id: 4,
      name: 'Serviette Microfibre',
      image: null,
      category: 'accessories',
      price: 800,
      stock: 15,
      lowStockThreshold: 5,
      sku: 'SRV-MCF-001',
      barcode: '123456789015',
      supplier: 'SportWear',
      costPrice: 500,
      description: 'Serviette microfibre absorbante, séchage rapide',
      location: 'Étagère C1'
    }
  ];

  // Ajout d'une notification - DÉSACTIVÉ
  const addNotification = (notification) => {
    // Fonction désactivée, ne fait rien mais renvoie un ID pour compatibilité
    return Date.now();
  };

  // Marquer une notification comme lue - DÉSACTIVÉ
  const markAsRead = (notificationId) => {
    // Fonction désactivée, ne fait rien
  };

  // Récupérer tous les employés
  const getEmployees = () => mockEmployees;
  
  // Récupérer un employé par ID
  const getEmployeeById = (id) => mockEmployees.find(emp => emp.id === id);
  
  // Récupérer tous les produits
  const getProducts = () => mockProducts;
  
  // Récupérer un produit par ID
  const getProductById = (id) => mockProducts.find(product => product.id === id);
  
  // Récupérer les produits en stock faible
  const getLowStockProducts = () => mockProducts.filter(product => product.stock <= product.lowStockThreshold);

  // Trouver quels équipements nécessitent une maintenance
  const getMaintenanceNeeded = () => {
    // Mock data pour les équipements nécessitant une maintenance
    return [
      { id: 5, name: 'Tapis de Course #3', lastMaintenance: '2024-05-15', issue: 'Bouton d\'arrêt défectueux' },
      { id: 12, name: 'Banc de Musculation #2', lastMaintenance: '2024-04-22', issue: 'Instabilité' },
      { id: 8, name: 'Machine à Câble', lastMaintenance: '2024-03-10', issue: 'Câble effiloché' }
    ];
  };

  // Mettre à jour les statistiques globales
  const updateGlobalStats = (newStats) => {
    setGlobalStats(prev => ({
      ...prev,
      ...newStats
    }));
  };

  // Récupérer tous les plans d'abonnement
  const getSubscriptionPlans = () => mockSubscriptionPlans;
  
  // Récupérer un plan par ID
  const getSubscriptionPlanById = (id) => mockSubscriptionPlans.find(plan => plan.id === id);
  
  // Récupérer tous les abonnements
  const getSubscriptions = () => mockSubscriptions;
  
  // Récupérer un abonnement par ID
  const getSubscriptionById = (id) => mockSubscriptions.find(subscription => subscription.id === id);
  
  // Récupérer les abonnements actifs
  const getActiveSubscriptions = () => mockSubscriptions.filter(subscription => subscription.status === 'active');
  
  // Récupérer les abonnements qui expirent cette semaine
  const getExpiringSubscriptions = () => {
    const nextWeek = new Date();
    nextWeek.setDate(nextWeek.getDate() + 7);
    
    return mockSubscriptions.filter(subscription => {
      const endDate = new Date(subscription.endDate);
      return endDate <= nextWeek && endDate >= new Date() && subscription.status === 'active';
    });
  };
  
  // Calculer le revenu total des abonnements
  const calculateTotalRevenue = () => {
    return mockSubscriptions
      .filter(subscription => subscription.status === 'active')
      .reduce((total, subscription) => total + subscription.price, 0);
  };

  // Exposer le contexte
  const value = {
    // Données
    selectedMember,
    setSelectedMember,
    selectedEmployee,
    setSelectedEmployee,
    selectedProduct,
    setSelectedProduct,
    notifications: [], // Tableau vide au lieu des vraies notifications
    unreadCount: 0, // Toujours 0 
    globalStats,
    
    // Fonctions d'employés
    getEmployees,
    getEmployeeById,
    
    // Fonctions de produits
    getProducts,
    getProductById,
    getLowStockProducts,
    
    // Fonctions d'abonnements
    getSubscriptionPlans,
    getSubscriptionPlanById,
    getSubscriptions,
    getSubscriptionById,
    getActiveSubscriptions,
    getExpiringSubscriptions,
    calculateTotalRevenue,
    
    // Autres fonctions - désactivées mais toujours disponibles pour compatibilité
    addNotification,
    markAsRead,
    getMaintenanceNeeded,
    updateGlobalStats
  };

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
};

export default AppDataContext; 